import React, { useState, useEffect } from 'react';
import { 
  Container, Row, Col, Form, Button, Card, 
  ProgressBar, Alert, Spinner, Modal
} from 'react-bootstrap';
import { initializeApp } from 'firebase/app';
import { getFirestore, collection, addDoc } from 'firebase/firestore';
import 'bootstrap/dist/css/bootstrap.min.css';
// import '../styles/internship.css'; // Comment out if file doesn't exist

const InternshipApplicationForm = () => {
  // Firebase Configuration
  const firebaseConfig = {
    apiKey: "AIzaSyAo8JAiRJUyz6ArJCHOCwEqHQEmfbWgtgA",
    authDomain: "olewin-academy.firebaseapp.com",
    projectId: "olewin-academy",
    storageBucket: "olewin-academy.appspot.com",
    messagingSenderId: "622543323365",
    appId: "1:622543323365:web:9a3e28d5c63850d60e940c",
    measurementId: "G-09WHHRMJRC"
  };

  // Initialize Firebase with error handling
  let app, db;
  try {
    app = initializeApp(firebaseConfig);
    db = getFirestore(app);
  } catch (error) {
    console.error("Firebase initialization error:", error);
  }

  // Form steps
  const totalSteps = 5;
  const [currentStep, setCurrentStep] = useState(1);
  const [progress, setProgress] = useState(20);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  // Form data state
  const [formData, setFormData] = useState({
    // Personal Information (Step 1)
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    gender: '',
    dateOfBirth: '',
    
    // Location Details (Step 2)
    address: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    
    // Academic Information (Step 3)
    highestQualification: '',
    institution: '',
    graduationYear: '',
    cgpa: '',
    department: '',
    majorSubjects: '',
    
    // Internship Preferences (Step 4)
    internshipArea: '',
    duration: '',
    startDate: '',
    endDate: '',
    workPreference: '', // Remote, Hybrid, On-site
    
    // Additional Information (Step 5)
    skills: '',
    experience: '',
    projectsCompleted: '',
    certifications: '',
    linkedInProfile: '',
    githubProfile: '',
    additionalInfo: ''
  });

  // Popular internship areas with future demand
  const internshipAreas = [
    "Artificial Intelligence & Machine Learning",
    "Data Science & Analytics",
    "Cloud Computing",
    "Blockchain Development",
    "Quantum Computing",
    "Software Development",
    "Cybersecurity",
    "Internet of Things (IoT)",
    "Augmented/Virtual Reality",
    "Robotics & Automation",
    "Geology & Geospatial Technologies",
    "Clean Energy & Sustainability",
    "Biotechnology & Genomics",
    "Financial Technology (FinTech)",
    "Digital Marketing",
    "UI/UX Design",
    "IIT-JAM Research",
    "Edge Computing",
    "Natural Language Processing",
    "Computer Vision"
  ];

  // Update progress when step changes
  useEffect(() => {
    setProgress(currentStep * (100 / totalSteps));
  }, [currentStep]);

  // Form input change handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  // Validate current step
  const validateStep = () => {
    const newErrors = {};
    
    if (currentStep === 1) {
      // Validate Step 1 fields
      if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
      if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
      if (!formData.email.trim()) {
        newErrors.email = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = "Please enter a valid email address";
      }
      if (!formData.phone.trim()) {
        newErrors.phone = "Phone number is required";
      } else if (!/^\d{10}$/.test(formData.phone.replace(/[^0-9]/g, ''))) {
        newErrors.phone = "Please enter a valid 10-digit phone number";
      }
      if (!formData.gender) newErrors.gender = "Please select your gender";
      if (!formData.dateOfBirth) newErrors.dateOfBirth = "Date of birth is required";
    }
    
    else if (currentStep === 2) {
      // Validate Step 2 fields
      if (!formData.address.trim()) newErrors.address = "Address is required";
      if (!formData.city.trim()) newErrors.city = "City is required";
      if (!formData.state.trim()) newErrors.state = "State is required";
      if (!formData.country.trim()) newErrors.country = "Country is required";
      if (!formData.postalCode.trim()) newErrors.postalCode = "Postal code is required";
    }
    
    else if (currentStep === 3) {
      // Validate Step 3 fields
      if (!formData.highestQualification) newErrors.highestQualification = "Qualification is required";
      if (!formData.institution.trim()) newErrors.institution = "Institution name is required";
      if (!formData.graduationYear) newErrors.graduationYear = "Graduation year is required";
      if (!formData.department.trim()) newErrors.department = "Department is required";
      if (!formData.cgpa.trim()) {
        newErrors.cgpa = "CGPA/Percentage is required";
      } else if (isNaN(formData.cgpa) || Number(formData.cgpa) < 0 || Number(formData.cgpa) > 10) {
        newErrors.cgpa = "Please enter a valid CGPA between 0-10";
      }
    }
    
    else if (currentStep === 4) {
      // Validate Step 4 fields
      if (!formData.internshipArea) newErrors.internshipArea = "Please select an internship area";
      if (!formData.duration) newErrors.duration = "Duration is required";
      if (!formData.startDate) newErrors.startDate = "Expected start date is required";
      if (!formData.workPreference) newErrors.workPreference = "Work preference is required";
    }
    
    // Step 5 has no mandatory fields
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Next step handler
  const handleNext = () => {
    const isValid = validateStep();
    if (isValid && currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  // Previous step handler
  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValid = validateStep();
    
    if (isValid && db) { // Check if db is initialized
      setIsSubmitting(true);
      
      try {
        // Submit data to Firebase
        await addDoc(collection(db, "InternshipApplications"), {
          ...formData,
          submittedAt: new Date(),
          status: "New"
        });
        
        setIsSubmitting(false);
        setShowSuccess(true);
        
        // Reset form after successful submission
        setTimeout(() => {
          setFormData({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            gender: '',
            dateOfBirth: '',
            address: '',
            city: '',
            state: '',
            country: '',
            postalCode: '',
            highestQualification: '',
            institution: '',
            graduationYear: '',
            cgpa: '',
            department: '',
            majorSubjects: '',
            internshipArea: '',
            duration: '',
            startDate: '',
            endDate: '',
            workPreference: '',
            skills: '',
            experience: '',
            projectsCompleted: '',
            certifications: '',
            linkedInProfile: '',
            githubProfile: '',
            additionalInfo: ''
          });
          setCurrentStep(1);
        }, 3000);
        
      } catch (error) {
        console.error("Error submitting application:", error);
        setIsSubmitting(false);
        setErrors({
          submission: "There was an error submitting your application. Please try again."
        });
      }
    } else if (!db) {
      setErrors({
        submission: "Database connection error. Please refresh the page and try again."
      });
    }
  };

  // Render form steps
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Card className="mb-4">
            <Card.Header className="bg-primary text-white">
              <h4>Personal Information</h4>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>First Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      isInvalid={!!errors.firstName}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.firstName}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Last Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      isInvalid={!!errors.lastName}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.lastName}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      isInvalid={!!errors.email}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone Number <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      isInvalid={!!errors.phone}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.phone}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Gender <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      isInvalid={!!errors.gender}
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Non-Binary">Non-Binary</option>
                      <option value="Prefer not to say">Prefer not to say</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.gender}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Date of Birth <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="date"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleChange}
                      isInvalid={!!errors.dateOfBirth}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.dateOfBirth}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        );
      
      case 2:
        return (
          <Card className="mb-4">
            <Card.Header className="bg-primary text-white">
              <h4>Location Details</h4>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Address <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      isInvalid={!!errors.address}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.address}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>City <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      isInvalid={!!errors.city}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.city}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>State/Province <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      isInvalid={!!errors.state}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.state}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Country <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      isInvalid={!!errors.country}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.country}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Postal/ZIP Code <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleChange}
                      isInvalid={!!errors.postalCode}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.postalCode}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        );
      
      case 3:
        return (
          <Card className="mb-4">
            <Card.Header className="bg-primary text-white">
              <h4>Academic Information</h4>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Highest Qualification <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      name="highestQualification"
                      value={formData.highestQualification}
                      onChange={handleChange}
                      isInvalid={!!errors.highestQualification}
                    >
                      <option value="">Select Qualification</option>
                      <option value="High School">High School</option>
                      <option value="Bachelor's Degree">Bachelor's Degree</option>
                      <option value="Master's Degree">Master's Degree</option>
                      <option value="Ph.D.">Ph.D.</option>
                      <option value="Diploma">Diploma</option>
                      <option value="Certificate Course">Certificate Course</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.highestQualification}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Institution Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="institution"
                      value={formData.institution}
                      onChange={handleChange}
                      isInvalid={!!errors.institution}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.institution}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Graduation Year <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="number"
                      name="graduationYear"
                      min="2000"
                      max="2030"
                      value={formData.graduationYear}
                      onChange={handleChange}
                      isInvalid={!!errors.graduationYear}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.graduationYear}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>CGPA/Percentage <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="cgpa"
                      placeholder="Enter on scale of 10"
                      value={formData.cgpa}
                      onChange={handleChange}
                      isInvalid={!!errors.cgpa}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.cgpa}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Department/Field of Study <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="department"
                      value={formData.department}
                      onChange={handleChange}
                      isInvalid={!!errors.department}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.department}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Major Subjects</Form.Label>
                    <Form.Control
                      type="text"
                      name="majorSubjects"
                      placeholder="Comma separated list"
                      value={formData.majorSubjects}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        );
      
      case 4:
        return (
          <Card className="mb-4">
            <Card.Header className="bg-primary text-white">
              <h4>Internship Preferences</h4>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Internship Area <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      name="internshipArea"
                      value={formData.internshipArea}
                      onChange={handleChange}
                      isInvalid={!!errors.internshipArea}
                    >
                      <option value="">Select Internship Area</option>
                      {internshipAreas.map((area, index) => (
                        <option key={index} value={area}>{area}</option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.internshipArea}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      These areas are in high demand for 2026-2027 and beyond
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Duration <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      name="duration"
                      value={formData.duration}
                      onChange={handleChange}
                      isInvalid={!!errors.duration}
                    >
                      <option value="">Select Duration</option>
                      <option value="1 Month">1 Month</option>
                      <option value="2 Months">2 Months</option>
                      <option value="3 Months">3 Months</option>
                      <option value="6 Months">6 Months</option>
                      <option value="Custom">Custom</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.duration}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Work Preference <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      name="workPreference"
                      value={formData.workPreference}
                      onChange={handleChange}
                      isInvalid={!!errors.workPreference}
                    >
                      <option value="">Select Preference</option>
                      <option value="Remote">Remote</option>
                      <option value="On-site">On-site</option>
                      <option value="Hybrid">Hybrid</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.workPreference}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Expected Start Date <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleChange}
                      isInvalid={!!errors.startDate}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.startDate}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Expected End Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        );
      
      case 5:
        return (
          <Card className="mb-4">
            <Card.Header className="bg-primary text-white">
              <h4>Additional Information</h4>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Skills & Technologies</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="skills"
                      placeholder="List your relevant skills, separated by commas"
                      value={formData.skills}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Previous Experience</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="experience"
                      placeholder="Briefly describe any relevant work or internship experience"
                      value={formData.experience}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Projects Completed</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="projectsCompleted"
                      placeholder="List your relevant projects"
                      value={formData.projectsCompleted}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Certifications</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="certifications"
                      placeholder="List any relevant certifications"
                      value={formData.certifications}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>LinkedIn Profile</Form.Label>
                    <Form.Control
                      type="url"
                      name="linkedInProfile"
                      placeholder="https://linkedin.com/in/yourusername"
                      value={formData.linkedInProfile}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>GitHub Profile</Form.Label>
                    <Form.Control
                      type="url"
                      name="githubProfile"
                      placeholder="https://github.com/yourusername"
                      value={formData.githubProfile}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Additional Information</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="additionalInfo"
                      placeholder="Anything else you would like us to know"
                      value={formData.additionalInfo}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        );
      
      default:
        return null;
    }
  };

  return (
    <>
      <Container className="py-5">
        <Row className="justify-content-center mb-5">
          <Col lg={10}>
            <Card className="shadow-lg border-0">
              <Card.Header className="bg-dark text-white text-center py-4">
                <h2 className="mb-0">Internship Application Form</h2>
                <p className="text-light mb-0">Join our Internship Program for 2026-2027</p>
              </Card.Header>
              <Card.Body className="p-4">
                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between mb-1">
                    <span>Step {currentStep} of {totalSteps}</span>
                    <span>{progress}% Completed</span>
                  </div>
                  <ProgressBar now={progress} variant="success" />
                </div>
                
                {/* Error Alert */}
                {errors.submission && (
                  <Alert variant="danger" className="mb-4">
                    {errors.submission}
                  </Alert>
                )}
                
                <Form onSubmit={handleSubmit}>
                  {/* Render current step */}
                  {renderStep()}
                  
                  {/* Navigation Buttons */}
                  <div className="d-flex justify-content-between mt-4">
                    <Button
                      variant="outline-secondary"
                      onClick={handlePrevious}
                      disabled={currentStep === 1 || isSubmitting}
                    >
                      Previous
                    </Button>
                    
                    {currentStep < totalSteps ? (
                      <Button
                        variant="primary"
                        onClick={handleNext}
                        disabled={isSubmitting}
                      >
                        Next
                      </Button>
                    ) : (
                      <Button
                        variant="success"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Submitting...
                          </>
                        ) : (
                          'Submit Application'
                        )}
                      </Button>
                    )}
                  </div>
                </Form>
              </Card.Body>
              <Card.Footer className="bg-light text-center py-3">
                <p className="text-muted mb-0">
                  <small>Fields marked with <span className="text-danger">*</span> are required</small>
                </p>
              </Card.Footer>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Success Modal */}
      <Modal
        show={showSuccess}
        onHide={() => setShowSuccess(false)}
        centered
        backdrop="static"
      >
        <Modal.Header className="bg-success text-white">
          <Modal.Title>Application Submitted!</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div className="mb-4">
            <div className="text-success" style={{ fontSize: "4rem" }}>✓</div>
          </div>
          <h4>Thank You for Applying!</h4>
          <p>
            Your internship application has been successfully submitted. Our team will review your application and contact you soon.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={() => setShowSuccess(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default InternshipApplicationForm;